import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Button, MenuItem, TextField, Typography, CircularProgress } from '@mui/material';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';

import { saveAs } from 'file-saver';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import AllPartyReportService from 'services/AllPartyReportService';
import { useTranslation } from 'react-i18next';
import { formatBalanceWithCr } from 'helpers/NumberFormatHelper';
import Info from 'components/utils/Alert/Info';
import { selectedTabContext } from 'Context/SelectedTabContext';

function AllPartyReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { setSelectedTab, setSelectedSubTab, setSections } = useContext(selectedTabContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [partyType, setPartyType] = useState('ALL');
  const [search, setSearch] = useState('');
  const [balanceFilter, setBalanceFilter] = useState('ALL');
  const [sorting, setSorting] = useState('name_asc');
  const [rawItems, setRawItems] = useState(null);

  const [appliedPartyType, setAppliedPartyType] = useState('ALL');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedBalanceFilter, setAppliedBalanceFilter] = useState('ALL');
  const [appliedSorting, setAppliedSorting] = useState('name_asc');

  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    name: tCommon('Name'),
    mobile: tCommon('Contact Number'),
    address: tCommon('Address'),
    party_type: tCommon('Party Type'),
    balance: {
      title: tCommon('Balance'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.balance || 0);
      },
    },
  };

  useEffect(() => {
    setSelectedTab('all-party-report');
    setSelectedSubTab('');
    setSections((prev) => ({
      ...prev,
      salesTab: false,
      purchaseTab: false,
      stockTab: false,
      customerTab: false,
      taxTab: false,
      ncTab: false,
    }));
    loadSummaryReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSummaryReports = async () => {
    setLoadingIndicator(true);
    try {
      const resp = await AllPartyReportService.getAllPartiesReport({});
      const formatted = (resp || []).map((item) => ({
        ...item,
        unique_id: `${item.party_type}_${item.id}`,
      }));
      setRawItems(formatted);
      setAppliedPartyType(partyType);
      setAppliedSearch(search);
      setAppliedBalanceFilter(balanceFilter);
      setAppliedSorting(sorting);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const generateSummaryReport = () => {
    setAppliedPartyType(partyType);
    setAppliedSearch(search);
    setAppliedBalanceFilter(balanceFilter);
    setAppliedSorting(sorting);
  };

  const isFiltersChanged = useMemo(() => {
    return (
      partyType !== appliedPartyType ||
      search !== appliedSearch ||
      balanceFilter !== appliedBalanceFilter ||
      sorting !== appliedSorting
    );
  }, [
    partyType,
    appliedPartyType,
    search,
    appliedSearch,
    balanceFilter,
    appliedBalanceFilter,
    sorting,
    appliedSorting,
  ]);

  const isShowGenerateButton = rawItems === null ? null : isFiltersChanged;

  const items = useMemo(() => {
    if (!rawItems) return [];

    let result = rawItems.filter((item) => {
      // 1. Party Type filter
      if (appliedPartyType === 'CUSTOMER' && item.party_type !== 'CUSTOMER') return false;
      if (appliedPartyType === 'VENDOR' && item.party_type !== 'VENDOR') return false;

      // 2. Balance filter: NEGATIVE (< 0), POSITIVE (> 0), ZERO (= 0), ALL
      if (appliedBalanceFilter === 'NEGATIVE' && item.balance >= 0) return false;
      if (appliedBalanceFilter === 'POSITIVE' && item.balance <= 0) return false;
      if (appliedBalanceFilter === 'ZERO' && item.balance !== 0) return false;

      // 3. Search filter: name, mobile, email, address
      if (appliedSearch && appliedSearch.trim() !== '') {
        const query = appliedSearch.toLowerCase();
        const name = (item.name || '').toLowerCase();
        const mobile = (item.mobile || '').toLowerCase();
        const address = (item.address || '').toLowerCase();
        if (!name.includes(query) && !mobile.includes(query) && !address.includes(query)) {
          return false;
        }
      }

      return true;
    });

    // 4. Sorting: Name, Balance, or Party Type
    result.sort((a, b) => {
      let comparison = 0;
      if (appliedSorting.startsWith('name')) {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (appliedSorting.startsWith('balance')) {
        comparison = a.balance - b.balance;
      } else if (appliedSorting.startsWith('type')) {
        const typeA = (a.party_type || '').toLowerCase();
        const typeB = (b.party_type || '').toLowerCase();
        comparison = typeA.localeCompare(typeB);
      }

      if (appliedSorting.endsWith('desc')) {
        return -comparison;
      }
      return comparison;
    });

    return result;
  }, [rawItems, appliedPartyType, appliedBalanceFilter, appliedSearch, appliedSorting]);

  const summary = useMemo(() => {
    let customerCount = 0;
    let vendorCount = 0;
    let toGet = 0;
    let toGive = 0;
    let totalBalance = 0;

    items.forEach((item) => {
      totalBalance += item.balance;
      if (item.party_type === 'CUSTOMER') {
        customerCount++;
        if (item.balance < 0) {
          toGet += Math.abs(item.balance);
        } else if (item.balance > 0) {
          toGive += item.balance;
        }
      } else if (item.party_type === 'VENDOR') {
        vendorCount++;
        if (item.balance < 0) {
          toGive += Math.abs(item.balance);
        } else if (item.balance > 0) {
          toGet += item.balance;
        }
      }
    });

    const profit = toGet - toGive;

    return {
      customerCount,
      vendorCount,
      toGetRaw: toGet,
      toGiveRaw: toGive,
      profitRaw: profit,
      totalBalance: ShopsHelper.getAmountFormatted(shop, totalBalance),
      toGet: ShopsHelper.getAmountFormatted(shop, toGet),
      toGive: ShopsHelper.getAmountFormatted(shop, toGive),
      profit: ShopsHelper.getAmountFormatted(shop, profit),
    };
  }, [items, shop]);
  const handlePrintReport = () => {
    handlePrintReportHook(
      tCommon('All Party Report'),
      null,
      null,
      headerData.filter((col) => col.id !== 'actions'),
      items,
      {
        [tCommon('no_of_parties')]: `${summary.customerCount} / ${summary.vendorCount}`,
        [tCommon('to_get')]: `${shop?.currency || ''} ${summary.toGet}`,
        [tCommon('to_give')]: `${shop?.currency || ''} ${summary.toGive}`,
        [tCommon('Profit')]: `${shop?.currency || ''} ${summary.profit}`,
        [tCommon('Total Balance')]: `${shop?.currency || ''} ${summary.totalBalance}`,
      }
    );
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(items, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `ALL-PARTY-REPORT-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const getBalance = (data) => {
    const balance = ShopsHelper.getAmountFormatted(shop, data?.balance || 0);
    let balanceStyle;
    if (data?.balance < 0) {
      balanceStyle = { color: 'red', fontWeight: '500' };
    } else if (data?.balance > 0) {
      balanceStyle = { color: 'green', fontWeight: '500' };
    } else {
      balanceStyle = { color: 'gray' };
    }
    return (
      <Typography variant="body2" style={balanceStyle} gutterBottom>
        {formatBalanceWithCr(balance)}
      </Typography>
    );
  };

  const getPartyTypeBadge = (type) => {
    const isCustomer = type === 'CUSTOMER';
    const badgeStyle = {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      backgroundColor: isCustomer ? 'rgba(56, 63, 193, 0.1)' : 'rgba(255, 133, 27, 0.1)',
      color: isCustomer ? '#383FC1' : '#ff851b',
      border: `1px solid ${isCustomer ? 'rgba(56, 63, 193, 0.2)' : 'rgba(255, 133, 27, 0.2)'}`,
    };
    return <span style={badgeStyle}>{isCustomer ? tCommon('Customer') : tCommon('Vendor')}</span>;
  };

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Contact Number'),
      id: 'mobile',
      type: 'text',
    },
    {
      label: tCommon('Address'),
      id: 'address',
      type: 'callback',
      viewRender: (item) => item.address || tCommon('N/A'),
    },
    {
      label: tCommon('Party Type'),
      id: 'party_type',
      type: 'callback',
      viewRender: (item) => getPartyTypeBadge(item.party_type),
    },
    {
      label: tCommon('Balance'),
      id: 'balance',
      type: 'callback',
      viewRender: (item) => getBalance(item),
    },
  ];

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>

        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>

        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>

      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>{tCommon('All Party Report')}</h2>
          </div>
        </div>

        {rawItems !== null && (
          <div className={styles.summaryContainer}>
            <div className={`${styles.summaryCard} ${styles.cardGreen}`}>
              <span className={styles.cardTitle}>{tCommon('to_get')}</span>
              <div className={styles.cardValue} style={{ color: 'green' }}>
                {summary.toGet}
              </div>
              <div className={styles.cardDetails}>
                <span>{tCommon('total_receivable')}</span>
              </div>
            </div>

            <div className={`${styles.summaryCard} ${styles.cardOrange}`}>
              <span className={styles.cardTitle}>{tCommon('to_give')}</span>
              <div className={styles.cardValue} style={{ color: '#ff851b' }}>
                {summary.toGive}
              </div>
              <div className={styles.cardDetails}>
                <span>{tCommon('total_payable')}</span>
              </div>
            </div>

            <div className={`${styles.summaryCard} ${summary.profitRaw >= 0 ? styles.cardGreen : styles.cardRed}`}>
              <span className={styles.cardTitle}>{tCommon('Balance')}</span>
              <div className={styles.cardValue} style={{ color: summary.profitRaw >= 0 ? 'green' : 'red' }}>
                {summary.profit}
              </div>
              <div className={styles.cardDetails}>
                <span>{summary.profitRaw >= 0 ? tCommon('net_receivable') : tCommon('net_payable')}</span>
              </div>
            </div>

            <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
              <span className={styles.cardTitle}>{tCommon('no_of_parties')}</span>
              <div className={styles.cardValue}>{summary.customerCount + summary.vendorCount}</div>
              <div className={styles.cardDetails}>
                <span>
                  {tCommon('Customer')}: <strong className={styles.detailsValue}>{summary.customerCount}</strong>
                </span>
                <span>•</span>
                <span>
                  {tCommon('Vendor')}: <strong className={styles.detailsValue}>{summary.vendorCount}</strong>
                </span>
              </div>
            </div>
          </div>
        )}

        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Party Type')}</label>
            </div>
            <div>
              <TextField
                select
                name="partyType"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 150 }}
                value={partyType}
                onChange={(event) => setPartyType(event.target.value)}
              >
                <MenuItem value="ALL">{tCommon('All')}</MenuItem>
                <MenuItem value="CUSTOMER">{tCommon('Customer')}</MenuItem>
                <MenuItem value="VENDOR">{tCommon('Vendor')}</MenuItem>
              </TextField>
            </div>
          </div>

          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Balance Status')}</label>
            </div>
            <div>
              <TextField
                select
                name="balanceFilter"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 200 }}
                value={balanceFilter}
                onChange={(event) => setBalanceFilter(event.target.value)}
              >
                <MenuItem value="ALL">{tCommon('All')}</MenuItem>
                <MenuItem value="NEGATIVE">{tCommon('Outstanding (-ve)')}</MenuItem>
                <MenuItem value="POSITIVE">{tCommon('Positive (+ve)')}</MenuItem>
                <MenuItem value="ZERO">{tCommon('Zero (0)')}</MenuItem>
              </TextField>
            </div>
          </div>

          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Sort')}</label>
            </div>
            <div>
              <TextField
                select
                name="sorting"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 200 }}
                value={sorting}
                onChange={(event) => setSorting(event.target.value)}
              >
                <MenuItem value="name_asc">{tCommon('Name')} (A-Z)</MenuItem>
                <MenuItem value="name_desc">{tCommon('Name')} (Z-A)</MenuItem>
                <MenuItem value="balance_desc">{tCommon('descending_by_balance')}</MenuItem>
                <MenuItem value="balance_asc">{tCommon('ascending_by_balance')}</MenuItem>
                <MenuItem value="type_asc">{tCommon('Party Type')} (A-Z)</MenuItem>
                <MenuItem value="type_desc">{tCommon('Party Type')} (Z-A)</MenuItem>
              </TextField>
            </div>
          </div>

          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Search')}</label>
            </div>
            <div>
              <TextField
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 220 }}
                placeholder={tCommon('Search') + '...'}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateSummaryReport}
                disabled={loadingIndicator || !isFiltersChanged}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isShowGenerateButton === null ? (
        <Info title={tReport('Set filters and click generate')} content={tReport('Report filters info')} />
      ) : items && items.length ? (
        <div className={styles.tableContainer}>
          <Grayout open={isShowGenerateButton} />
          <DataTable
            columns={headerData}
            rows={items}
            rowKey="unique_id"
            pagination={true}
            summary={{
              name: tCommon('Total'),
              balance: `${shop?.currency || ''} ${summary.totalBalance}`,
            }}
          />
        </div>
      ) : (
        !loadingIndicator && (
          <Info
            severity="warning"
            title={tCommon('No records found')}
            content={tCommon('No Customer or Vendor records match the filter criteria.')}
          />
        )
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(AllPartyReport);
